import React, { useEffect, useState } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import MetricChart from './MetricChart';

// Interface for energy data from the API
interface EnergyEntry {
  id: number;
  level: 1 | 2 | 3; // Energy level: 1 (Low), 2 (Medium), 3 (High)
  comment?: string | null;
  created_at: string; // ISO date string
}

// Interface for the data format Recharts expects
interface ChartData {
  date: string; // Formatted date for XAxis
  level: number;
  comment?: string | null;
  fullDate: string; // For tooltip
  originalDate: Date; // For sorting
}

const energyLabels: { [key: number]: string } = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
};

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as ChartData;
    return (
      <div style={{
        padding: SPACING[8],
        backgroundColor: COLORS.bg.panel,
        border: `1px solid ${COLORS.border.hover}`,
        borderRadius: RADIUS,
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      }}>
        <p style={{
          fontSize: TYPOGRAPHY.size.sm,
          fontWeight: TYPOGRAPHY.weight.semibold,
          color: COLORS.text.secondary,
          margin: 0,
        }}>{`Date: ${data.fullDate}`}</p>
        <p style={{
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.text.secondary,
          margin: 0,
        }}>{`Energy: ${energyLabels[data.level] || data.level}`}</p>
        {data.comment && (
          <p style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.size.xs,
            color: COLORS.text.tertiary,
            fontStyle: 'italic',
          }}>{`Comment: ${data.comment}`}</p>
        )}
      </div>
    );
  }
  return null;
};

const EnergyTracker: React.FC = () => {
  const [energyData, setEnergyData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEnergyData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/api/energy/');

      if (response.status >= 200 && response.status < 300) {
        const data: EnergyEntry[] = response.data;

        const mappedData: (ChartData | null)[] = data.map(entry => {
          if (!entry || typeof entry.created_at !== 'string' || entry.created_at.trim() === '') {
            console.warn('Skipping energy entry due to missing or invalid created_at:', entry);
            return null;
          }
          try {
            const parsedDate = parseISO(entry.created_at);
            if (isNaN(parsedDate.getTime())) {
              console.warn('Skipping energy entry due to unparseable created_at string:', entry.created_at, entry);
              return null;
            }
            return {
              date: format(parsedDate, 'MMM d'),
              level: entry.level,
              comment: entry.comment,
              fullDate: format(parsedDate, 'MMM d, yyyy'),
              originalDate: parsedDate,
            };
          } catch (dateProcessingError) {
            console.error('Error processing date for energy entry:', entry.created_at, entry, dateProcessingError);
            return null;
          }
        });

        // Filter out nulls using a type guard
        const filteredData: ChartData[] = mappedData.filter(
          (item): item is ChartData => item !== null
        );

        // Sort data by date in ascending order (oldest to newest)
        filteredData.sort((a, b) => {
          const dateA = a.originalDate.getTime();
          const dateB = b.originalDate.getTime();
          return dateA - dateB;
        });

        // Filter to show only last 30 days
        const thirtyDaysAgo = subDays(new Date(), 30);
        const last30DaysData = filteredData.filter(item => item.originalDate >= thirtyDaysAgo);

        setEnergyData(last30DaysData);
      } else {
        throw new Error(`Failed to fetch energy data: ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching energy data:', err);
      setError('Failed to load energy data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnergyData();

    // Listen for energy refresh events
    const handleEnergyRefresh = () => {
      fetchEnergyData();
    };

    window.addEventListener('energyDataUpdated', handleEnergyRefresh);

    return () => {
      window.removeEventListener('energyDataUpdated', handleEnergyRefresh);
    };
  }, []);

  // Card base styles
  const cardStyle: React.CSSProperties = {
    backgroundColor: COLORS.bg.panel,
    border: `1px solid ${COLORS.border.default}`,
    borderRadius: RADIUS,
    padding: SPACING[16],
    height: '320px',
  };

  if (loading) {
    return (
      <div style={{
        ...cardStyle,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <ArrowPathIcon style={{
          width: '32px',
          height: '32px',
          color: COLORS.text.tertiary,
          animation: 'spin 1s linear infinite',
          marginBottom: SPACING[8],
        }} />
        <p style={{
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.text.tertiary,
          margin: 0,
        }}>Loading energy data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        ...cardStyle,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[16],
      }}>
        <ExclamationCircleIcon style={{
          width: '32px',
          height: '32px',
          color: COLORS.status.error,
          marginBottom: SPACING[8],
        }} />
        <p style={{
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.status.error,
          textAlign: 'center',
          margin: 0,
        }}>{error}</p>
      </div>
    );
  }

  if (energyData.length === 0) {
    return (
      <div style={{
        ...cardStyle,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <p style={{
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.text.tertiary,
          margin: 0,
        }}>No energy data recorded yet.</p>
        <p style={{
          fontSize: TYPOGRAPHY.size.xs,
          color: COLORS.text.tertiary,
          marginTop: SPACING[4],
        }}>Start tracking your energy using the Quick Add menu!</p>
      </div>
    );
  }

  return (
    <div style={{
      ...cardStyle,
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
    }}>
      <h3 style={{
        fontSize: TYPOGRAPHY.headings.sizes.h2,
        fontFamily: TYPOGRAPHY.headings.fontFamily,
        fontWeight: TYPOGRAPHY.weight.medium,
        color: COLORS.text.primary,
        margin: 0,
        marginBottom: SPACING[16],
        paddingLeft: SPACING[16],
        paddingRight: SPACING[16],
        paddingTop: SPACING[16],
      }}>Energy Evolution (Last 30 days)</h3>
      <div style={{ flexGrow: 1, paddingLeft: SPACING[8], paddingRight: SPACING[8] }}>
        <MetricChart
          data={energyData}
          series={[{ dataKey: 'level', name: 'Energy Level', metricType: 'wellness', color: COLORS.status.success }]}
          height={200}
          showLegend={false}
          customTooltip={CustomTooltip}
          yDomain={[1, 3]}
          yTicks={[1, 2, 3]}
          yTickFormatter={(v) => ({ 1: 'Low', 2: 'Medium', 3: 'High' }[v] || '')}
        />
      </div>
    </div>
  );
};

export default EnergyTracker;
